import React, {useState, useRef} from 'react';
import {Helmet} from 'react-helmet';
import SignatureCanvas from 'react-signature-canvas';
import { PaymentComponent } from '../../components/PaymentComponent';
import '../../assets/styles/regenpod/regenpod-business.scss';
import '../../assets/styles/regenpod/profit-calculator-addon.scss';

export const RegenPodBusiness: React.FC = () => {
  const [formData, setFormData] = useState({
    code: ''
  });

  const [isVerified, setIsVerified] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDepositConfirmed, setIsDepositConfirmed] = useState(false);
  const [showDoLaterModal, setShowDoLaterModal] = useState(false);
  const [doLaterCode, setDoLaterCode] = useState('');
  const [doLaterError, setDoLaterError] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [showPayment, setShowPayment] = useState(false);
  const [monthlySessions, setMonthlySessions] = useState(100);
  const signatureRef = useRef<SignatureCanvas>(null);
  const selectedTierRef = useRef<HTMLDivElement>(null);
  const calculatorRef = useRef<HTMLDivElement>(null);

  const tiers = [
    {
      id: "TIER1",
      name: 'Tier 1 - Starter',
      description: 'For new sites or smaller clinics ready to introduce regenerative technology.',
      monthlyFee: '£1,200',
      partnerShare: '£38',
      breakEven: '32'
    },
    {
      id: "TIER2",
      name: 'Tier 2 - Growth',
      description: 'Balances investment with rapid return potential — ideal for most wellness and performance centres.',
      monthlyFee: '£1,600',
      partnerShare: '£42',
      breakEven: '39'
    },
    {
      id: "TIER3",
      name: 'Tier 3 - Premium',
      description: 'Designed for high-volume environments focused on maximising capacity and revenue.',
      monthlyFee: '£2,400',
      partnerShare: '£49',
      breakEven: '49'
    }
  ];

  const profitData = {
    50: { TIER1: 630, TIER2: 500, TIER3: 50 },
    100: { TIER1: 2380, TIER2: 2600, TIER3: 2500 },
    173: { TIER1: 4935, TIER2: 5666, TIER3: 5907 },
    260: { TIER1: 7980, TIER2: 9320, TIER3: 10340 }
  } as const;

  const getRecommendedTier = (sessions: number) => {
    if (sessions >= 35 && sessions <= 70) return { tier: 'TIER1', name: 'Tier 1 (Foundation)', reason: 'Lowest cost, fastest breakeven' };
    if (sessions >= 75 && sessions <= 115) return { tier: 'TIER2', name: 'Tier 2 (Growth)', reason: 'Best profit-to-risk balance' };
    if (sessions >= 120) return { tier: 'TIER3', name: 'Tier 3 (Premium)', reason: 'Highest ROI per session' };
    return null;
  };

  const calculateProfit = (sessions: number, tier: string) => {
    const keys = [50, 100, 173, 260];
    
    if (sessions <= 50) return profitData[50][tier as keyof typeof profitData[50]];
    if (sessions >= 260) return profitData[260][tier as keyof typeof profitData[260]];
    
    for (let i = 0; i < keys.length - 1; i++) {
      const lower = keys[i];
      const upper = keys[i + 1];
      
      if (sessions >= lower && sessions <= upper) {
        const lowerProfit = profitData[lower as keyof typeof profitData][tier as keyof typeof profitData[50]];
        const upperProfit = profitData[upper as keyof typeof profitData][tier as keyof typeof profitData[50]];
        const ratio = (sessions - lower) / (upper - lower);
        return Math.round(lowerProfit + (upperProfit - lowerProfit) * ratio);
      }
    }
    
    return 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('https://api.regenphd.com/regenpod-code-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: formData.code })
      });
      const data = await response.json();
      
      if (data.verified) {
        setIsVerified(true);
        setUser(data.user);
        if (data.user.enrolled) {
          setShowPayment(true);
        }
      } else {
        if (data.isExpired) {
          setError('Code has expired - Partnership offer has expired. Please contact ruth.vigor30@gmail.com for new opportunities.');
        } else {
          setError(data.error || 'Invalid access code. Please try again.');
        }
      }
    } catch (error) {
      setError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDoLater = async () => {
    try {
      setDoLaterError('');
      const response = await fetch('https://api.regenphd.com/regenpod-partner-later', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ _id: user?._id, code: doLaterCode, tier: selectedTier })
      });
      const data = await response.json();
      if (!response.ok) {
        setDoLaterError(data.error || 'Payment setup failed');
      } else {
        setShowDoLaterModal(false);
        setMessage('Request saved. User can complete this partnership order later within 7 days.');
        setMessageType('success');
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setDoLaterError('Failed to save request');
    }
  };

  const handlePartnershipSubmit = async () => {
    if (!selectedTier || !isConfirmed || !isDepositConfirmed || !signatureRef.current || signatureRef.current?.isEmpty()) {
      setMessage('Please complete all required fields');
      setMessageType('error');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    setIsSubmitting(true);
    try {
      const signature = signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
      await fetch(`https://api.regenphd.com/regenpod-partner-enroll/${user?._id}`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ signature, tier: selectedTier})
      });
      setShowPayment(true);
    } catch (error) {
      setMessage('Failed to submit partnership agreement');
      setMessageType('error');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tierIndex = tiers.findIndex(tier => tier.id === selectedTier);

  return (
    <>
      <Helmet>
        <title>Regen PhD Pod Partnership | Engineer Your Biology. Regenerate by Design.</title>
        <meta name="description" content="Join the world's first regenerative technology platform designed for clinics ready to lead the future of wellness. Partner with Regen PhD Pod." />
      </Helmet>
      <div className="regenpod-business-container">
        {message && (
          <div className={`message-banner ${messageType === 'success' ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
        
        {showPayment ? (
          <PaymentComponent 
            onBack={() => setShowPayment(false)} 
            user={user} 
          />
        ) : !isVerified ? (
          <div className="partner-form-section" id="partner-form">
            <div className="form-container">
              <h2 className="section-title font-bold mb-2">Partner Access</h2>
              <p className="access-description">Access detailed ROI info and exclusive partner resources</p>
              <p className="form-description">
                Enter your partner access code to view detailed partnership information.
              </p>

              <form onSubmit={handleSubmit} className="partner-form">
                <input
                  type="text"
                  name="code"
                  placeholder="Enter Partner Access Code *"
                  value={formData.code}
                  onChange={handleInputChange}
                  required
                />
                
                {error && <div className="error-message">{error}</div>}
                
                <button type="submit" className="submit-btn font-semibold" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Access Partner Information'}
                </button>
              </form>

              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-label font-semibold">Phone:</span>
                  <a href="tel:03300010048" className="contact-link">0330 001 0048</a>
                </div>
                <div className="contact-item">
                  <span className="contact-label font-semibold">Email:</span>
                  <a href="mailto:pod@regenphd.com" className="contact-link">pod@regenphd.com</a>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>

            <div className="partnership-tiers-section">

              <h2 className="section-title font-bold">Partnership Tiers</h2>

              <div className="hero-content">
                {user?.name && (
                  <div className="welcome-banner">
                    <h3 className="welcome-title">Welcome, {user.name}!</h3>
                    <p className="welcome-subtitle">
                      Join the UK's most advanced regenerative technology network and start your partnership today.
                    </p>
                  </div>
                )}
              </div>
              <div className="profit-calculator" ref={calculatorRef}>
                <h3 className="calculator-title">Monthly Profit Calculator</h3>
                <div className="calculator-content">
                  <div className="sessions-input">
                    <div className="sessions-header">
                      <label htmlFor="sessions" className="font-semibold">Monthly Sessions:</label>
                      <input
                          type="number"
                          min="35"
                          max="260"
                          value={monthlySessions}
                          onChange={(e) => setMonthlySessions(Number(e.target.value))}
                          className="sessions-textbox font-semibold"
                      />
                    </div>
                    <input
                        id="sessions"
                        type="range"
                        min="35"
                        max="260"
                        value={monthlySessions}
                        onChange={(e) => setMonthlySessions(Number(e.target.value))}
                        className="sessions-slider"
                    />
                    <div className="sessions-range">
                      <span className="range-min font-md">Min 35</span>
                      <span className="range-max font-md">Max 260</span>
                    </div>
                  </div>

                  <div className="profit-display">
                    <div className="profit-grid">
                      {tiers.map((tier) => {
                        const profit = calculateProfit(monthlySessions, tier.id);
                        const isSelected = selectedTier === tier.id;
                        const recommendation = getRecommendedTier(monthlySessions);
                        const isRecommended = recommendation?.tier === tier.id;
                        return (
                            <div key={tier.id} className={`profit-card ${isSelected ? 'selected' : ''} ${isRecommended ? 'recommended' : ''}`}>
                              {isRecommended && <div className="recommended-tag">Recommended</div>}
                              <div className="profit-tier">{tier.name.split(' - ')[0]}</div>
                              <div className="profit-amount">£{profit.toLocaleString()}</div>
                              {isRecommended && <div className="recommended-desc">{recommendation.reason}</div>}
                            </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="tiers-selection">
                {tiers.map((tier) => (
                  <div 
                    key={tier.id}
                    className={`tier-option ${selectedTier === tier.id ? 'selected' : ''} ${tier.id === "TIER3" ? 'popular' : ''}`}
                    onClick={() => {
                      setSelectedTier(tier.id);
                      setTimeout(() => {
                        selectedTierRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                  >
                    <div className="tier-header">
                      <h3 className="tier-name">{tier.name}</h3>
                      {tier.id === "TIER3" && (
                        <span className="popular-badge">
                          Popular
                        </span>
                      )}
                    </div>
                    <p className="tier-description">{tier.description}</p>
                    <div className="tier-pricing">
                      <div className="pricing-item">
                        <span className="label">Monthly Fee:</span>
                        <span className="value">{tier.monthlyFee}</span>
                      </div>
                      <div className="pricing-item">
                        <span className="label">Partner Share:</span>
                        <span className="value">{tier.partnerShare}</span>
                      </div>
                      <div className="pricing-item">
                        <span className="label">Break Even / <sub className="font-legacy">month</sub>:</span>
                        <span className="value">{tier.breakEven} sessions</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedTier && tierIndex > -1 && (
                <div className="selected-tier-details" ref={selectedTierRef}>
                  <h3 className="details-title">Your Selected Partnership for 24 months</h3>
                  <div className="tier-summary">
                    <h4 className="summary-title">{tiers[tierIndex].name}</h4>
                    <p className="summary-description">{tiers[tierIndex].description}</p>
                    
                    <div className="summary-grid">
                      <div className="summary-item">
                        <div className="item-label">Monthly Investment</div>
                        <div className="item-value">{tiers[tierIndex].monthlyFee}</div>
                      </div>
                      <div className="summary-item">
                        <div className="item-label">Your Share per Session</div>
                        <div className="item-value share-value">{tiers[tierIndex].partnerShare}</div>
                      </div>
                      <div className="summary-item">
                        <div className="item-label">Break Even Point / <sub className="font-legacy">month</sub></div>
                        <div className="item-value break-even-value">{tiers[tierIndex].breakEven} sessions</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedTier && (
                <>
                  <div className="partnership-agreement">
                    <div className="agreement-checkbox">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={isDepositConfirmed} 
                          onChange={(e) => setIsDepositConfirmed(e.target.checked)}
                        />
                        <span className="checkbox-text">
                          A £2,500 activation deposit is required to confirm your partnership. 
                          This covers Delivery, Installation, System Setup and In-Clinic training. 
                          Deposits are Non-Refundable and secure your place in current rollout phase.
                        </span>
                      </label>
                    </div>
                    <div className="agreement-checkbox">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={isConfirmed} 
                          onChange={(e) => setIsConfirmed(e.target.checked)}
                        />
                        <span className="checkbox-text">
                          I confirm that I have read and agree to the partnership terms, understand the deposit requirements, 
                          and authorize the selected tier partnership with Regen PhD Pod.
                        </span>
                      </label>
                    </div>

                    <div className="signature-section">
                      <label className="signature-label">Signature *</label>
                      <div className="signature-canvas">
                        <SignatureCanvas 
                          ref={signatureRef}
                          canvasProps={{
                            width: window.innerWidth  > 1200 ? 1200 : window.innerWidth - 100,
                            height: 200,
                            className: 'signature-pad',
                            style: { height: '200px' }
                          }}
                        />
                      </div>
                      <button 
                        type="button" 
                        onClick={() => signatureRef.current?.clear()}
                        className="clear-signature-btn"
                      >
                        Clear Signature
                      </button>
                    </div>

                    <div className="action-buttons">
                      <button 
                        onClick={handlePartnershipSubmit}
                        className="submit-partnership-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Partnership Agreement'}
                      </button>
                      <button 
                        onClick={() => setShowDoLaterModal(true)}
                        className="do-later-btn"
                        disabled={isSubmitting}
                      >
                        Admin (Allow Me to Do It Later)
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {showDoLaterModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3 className="modal-title">Confirm "Admin"</h3>
              <p className="modal-description">Enter the access code to save your progress:</p>
              <input 
                type="text"
                value={doLaterCode}
                onChange={(e) => setDoLaterCode(e.target.value)}
                placeholder="Enter access code"
                className="modal-input"
              />
              {doLaterError && <p className="modal-error">{doLaterError}</p>}
              <div className="modal-buttons">
                <button 
                  onClick={() => setShowDoLaterModal(false)}
                  className="modal-cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDoLater}
                  disabled={!doLaterCode}
                  className="modal-confirm-btn"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};